import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePortal } from '../context/PortalContext';
import { useSupportContext } from '../context/SupportContext';
import { useOnboarding } from '../context/OnboardingContext';
import { useI18n } from '../context/I18nContext';
import {
  LayoutDashboard,
  FileText,
  Gavel,
  FolderOpen,
  Mail,
  TrendingUp,
  GraduationCap,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Import,
  BarChart3,
  Zap,
  Calculator,
  CalendarCheck,
  LineChart,
  CreditCard,
  Wand2,
  Map,
  Users,
  Building2,
  Shield,
  Home,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import NotificationDropdown from '../components/notifications/NotificationDropdown';
import ProfileMenuDropdown from '../components/profile/ProfileMenuDropdown';
import SupportModeBanner from '../components/platform/SupportModeBanner';
import { getPortalFromHostname, PORTAL_SURFACES } from '../utils/hostnamePortal';
import FundEliteBrandMark from '../components/branding/FundEliteBrandMark';

/**
 * Theme Context for Light/Dark/System support
 */
const ThemeContext = createContext({
  theme: 'light',
  resolvedTheme: 'light',
  setTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('fundelite-theme') || 'system';
    }
    return 'system';
  });
  
  const [resolvedTheme, setResolvedTheme] = useState('light');
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const updateResolvedTheme = () => {
      if (theme === 'system') {
        setResolvedTheme(mediaQuery.matches ? 'dark' : 'light');
      } else {
        setResolvedTheme(theme);
      }
    };
    
    updateResolvedTheme();
    mediaQuery.addEventListener('change', updateResolvedTheme);
    
    return () => mediaQuery.removeEventListener('change', updateResolvedTheme);
  }, [theme]);
  
  useEffect(() => {
    localStorage.setItem('fundelite-theme', theme);
    // CRITICAL: Set BOTH data-theme attribute AND class for full CSS compatibility
    // This ensures all CSS selectors work regardless of which pattern they use
    document.documentElement.setAttribute('data-theme', resolvedTheme);
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(resolvedTheme);
  }, [theme, resolvedTheme]);
  
  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * FundElite Premium Theme System
 * Reference: FundElite Credit Report - Improved Preview HTML
 * Light Mode: Airy mint premium fintech
 * Dark Mode: Deep emerald glass system with coordinated surfaces
 */
export const getThemeTokens = (resolvedTheme) => ({
  // Meta
  isDark: resolvedTheme === 'dark',
  
  // Core backgrounds
  bg: {
    light: { bg0: '#F7F9F8', bg1: '#F2FCF7', bg2: '#ECF8F1' },
    dark: { bg0: '#081610', bg1: '#0B1F18', bg2: '#0F2A21' },
  }[resolvedTheme],
  
  // Text colors
  text: {
    light: { primary: '#111827', secondary: '#4B5563', muted: '#6B7280' },
    dark: { primary: '#EAF7F0', secondary: '#C7DDD0', muted: '#93AE9F' },
  }[resolvedTheme],
  
  // Surface colors (cards, panels)
  surface: {
    light: {
      s1: 'rgba(255,255,255,0.84)',
      s2: 'rgba(255,255,255,0.76)',
      s3: 'rgba(255,255,255,0.66)',
    },
    dark: {
      s1: 'rgba(7,28,21,0.62)',
      s2: 'rgba(10,34,25,0.58)',
      s3: 'rgba(12,38,28,0.54)',
    },
  }[resolvedTheme],
  
  // Border colors
  border: {
    light: { b1: 'rgba(255,255,255,0.44)', b2: 'rgba(15,172,108,0.12)', soft: 'rgba(15,172,108,0.10)' },
    dark: { b1: 'rgba(142,218,182,0.12)', b2: 'rgba(142,218,182,0.10)', soft: 'rgba(142,218,182,0.10)' },
  }[resolvedTheme],
  
  // Icon color mapping (key update from reference)
  icon: {
    light: { neutral: '#5F6F64', primary: '#0D9F64', warn: '#EAB23A', danger: '#EF4444' },
    dark: { neutral: '#9DB3A7', primary: '#56B988', warn: '#EAB23A', danger: '#EF4444' },
  }[resolvedTheme],
  
  // Sidebar - branded anchor in both modes
  sidebar: {
    light: {
      gradient: 'linear-gradient(180deg, #0FAC6C 0%, #0D9F64 56%, #08754A 100%)',
      text: '#FFFFFF',
      textMuted: 'rgba(255,255,255,0.78)',
      activeBg: 'rgba(255,255,255,0.16)',
      hoverBg: 'rgba(255,255,255,0.08)',
    },
    dark: {
      gradient: 'linear-gradient(180deg, #06281f 0%, #073f2f 45%, #035f43 100%)',
      text: '#FFFFFF',
      textMuted: 'rgba(255,255,255,0.78)',
      activeBg: 'rgba(255,255,255,0.14)',
      hoverBg: 'rgba(255,255,255,0.08)',
    },
  }[resolvedTheme],
  
  // Top bar - glass treatment
  topBar: {
    light: {
      bg: 'rgba(255,255,255,0.64)',
      border: 'rgba(255,255,255,0.44)',
      text: '#111827',
      textMuted: '#6B7280',
      blur: 'blur(18px) saturate(140%)',
      shadow: '0 1px 3px rgba(16,24,40,0.06)',
    },
    dark: {
      bg: 'rgba(7,32,24,0.52)',
      border: 'rgba(142,218,182,0.12)',
      text: '#EAF7F0',
      textMuted: '#93AE9F',
      blur: 'blur(18px) saturate(140%)',
      shadow: '0 1px 3px rgba(0,0,0,0.25)',
    },
  }[resolvedTheme],
  
  // Main content area / page background (legacy compatibility)
  content: {
    light: {
      bg: '#F7F9F8',
      bgSecondary: '#F2FCF7',
      surface: 'rgba(255,255,255,0.84)',
      surfaceSecondary: 'rgba(255,255,255,0.76)',
      surfaceTertiary: 'rgba(255,255,255,0.66)',
      border: 'rgba(255,255,255,0.44)',
      borderSubtle: 'rgba(15,172,108,0.12)',
      text: '#111827',
      textSecondary: '#4B5563',
      textMuted: '#6B7280',
      shadow: '0 14px 36px rgba(16,24,40,0.08)',
      shadowLight: '0 2px 8px rgba(16,24,40,0.04)',
    },
    dark: {
      bg: '#081610',
      bgSecondary: '#0B1F18',
      surface: 'rgba(7,28,21,0.62)',
      surfaceSecondary: 'rgba(10,34,25,0.58)',
      surfaceTertiary: 'rgba(12,38,28,0.54)',
      border: 'rgba(142,218,182,0.12)',
      borderSubtle: 'rgba(142,218,182,0.10)',
      text: '#EAF7F0',
      textSecondary: '#C7DDD0',
      textMuted: '#93AE9F',
      shadow: '0 18px 48px rgba(0,0,0,0.26)',
      shadowLight: '0 4px 16px rgba(0,0,0,0.15)',
    },
  }[resolvedTheme],
  
  // Card surfaces
  card: {
    light: {
      fill: 'rgba(255,255,255,0.84)',
      border: 'rgba(255,255,255,0.44)',
      shadow: '0 14px 36px rgba(16,24,40,0.08)',
      hover: 'rgba(255,255,255,0.92)',
    },
    dark: {
      fill: 'rgba(7,28,21,0.62)',
      border: 'rgba(142,218,182,0.12)',
      shadow: '0 18px 48px rgba(0,0,0,0.26)',
      hover: 'rgba(10,34,25,0.72)',
    },
  }[resolvedTheme],
  
  // Table surfaces (updated from reference)
  table: {
    light: {
      container: 'rgba(255,255,255,0.84)',
      header: 'rgba(255,255,255,0.60)',
      row: 'rgba(255,255,255,0.52)',
      rowAlt: 'rgba(247,252,249,0.74)',
      rowHover: 'rgba(15,172,108,0.04)',
      border: 'rgba(15,172,108,0.10)',
      text: '#111827',
      textMuted: '#6B7280',
    },
    dark: {
      container: 'rgba(7,28,21,0.62)',
      header: 'rgba(255,255,255,0.04)',
      row: 'rgba(255,255,255,0.03)',
      rowAlt: 'rgba(255,255,255,0.045)',
      rowHover: 'rgba(142,218,182,0.06)',
      border: 'rgba(142,218,182,0.10)',
      text: '#EAF7F0',
      textMuted: '#93AE9F',
    },
  }[resolvedTheme],
  
  // Input/filter surfaces
  input: {
    light: {
      bg: 'rgba(255,255,255,0.72)',
      border: 'rgba(15,172,108,0.12)',
      text: '#111827',
      placeholder: '#9CA3AF',
      focus: 'rgba(15,172,108,0.20)',
    },
    dark: {
      bg: 'rgba(10,34,25,0.54)',
      border: 'rgba(142,218,182,0.10)',
      text: '#EAF7F0',
      placeholder: '#6B8878',
      focus: 'rgba(142,218,182,0.20)',
    },
  }[resolvedTheme],
  
  // Banner surfaces (import banner, alerts)
  banner: {
    light: {
      success: { bg: 'rgba(255,255,255,0.84)', border: 'rgba(255,255,255,0.44)', text: '#111827', textMuted: '#6B7280' },
      info: { bg: 'rgba(255,255,255,0.84)', border: 'rgba(255,255,255,0.44)', text: '#111827', textMuted: '#6B7280' },
      warning: { bg: 'rgba(254,243,199,0.70)', border: 'rgba(245,158,11,0.25)', text: '#92400E', textMuted: '#D97706' },
    },
    dark: {
      success: { bg: 'rgba(7,28,21,0.62)', border: 'rgba(142,218,182,0.12)', text: '#EAF7F0', textMuted: '#93AE9F' },
      info: { bg: 'rgba(7,28,21,0.62)', border: 'rgba(142,218,182,0.12)', text: '#EAF7F0', textMuted: '#93AE9F' },
      warning: { bg: 'rgba(120,53,15,0.35)', border: 'rgba(245,158,11,0.20)', text: '#FDE68A', textMuted: '#FBBF24' },
    },
  }[resolvedTheme],
  
  // Section tab bar (flow bar with gradient)
  tabBar: {
    bg: 'linear-gradient(135deg, #063D2D 0%, #08754A 45%, #0D9F64 100%)',
    border: 'rgba(15,172,108,0.16)',
    shadow: '0 14px 32px rgba(6,61,45,0.14)',
    activeBg: 'rgba(255,255,255,0.94)',
    activeText: '#1F2937',
    inactiveText: '#EAF7F0',
    chevronOpacity: 0.56,
  },
  
  // Green accent colors
  green: {
    500: '#0FAC6C',
    600: '#0D9F64',
    700: '#0B8C58',
    800: '#08754A',
    900: '#063D2D',
  },
  
  // Tag colors (from reference)
  tag: {
    red: { bg: 'rgba(239,68,68,0.12)', color: '#F27A7A' },
    gold: { bg: 'rgba(234,178,58,0.16)', color: '#F0C160' },
    green: { bg: 'rgba(15,172,108,0.12)', color: '#7FD7A8' },
  },
  
  // Status pill
  statusPill: {
    light: { bg: 'rgba(15,172,108,0.12)', color: '#0B8C58' },
    dark: { bg: 'rgba(15,172,108,0.18)', color: '#9AE3BF' },
  }[resolvedTheme],
  
  // Bureau pill
  bureauPill: {
    light: { bg: 'rgba(0,0,0,0.04)', border: 'rgba(15,172,108,0.12)', color: '#4B5563' },
    dark: { bg: 'rgba(255,255,255,0.08)', border: 'rgba(142,218,182,0.10)', color: '#C7DDD0' },
  }[resolvedTheme],
  
  // Check mark box
  checkBox: {
    bg: 'rgba(86,185,136,0.16)',
    border: 'rgba(86,185,136,0.36)',
    color: '#8DE5B7',
  },
  
  // Action colors
  action: {
    primary: '#62D09B',
  },
  
  // Empty state / positive box
  positiveBox: {
    light: { bg: 'rgba(15,172,108,0.10)', border: 'rgba(15,172,108,0.16)', color: '#0B8C58' },
    dark: { bg: 'rgba(15,172,108,0.12)', border: 'rgba(15,172,108,0.16)', color: '#8FE0B7' },
  }[resolvedTheme],
  
  // Mobile overlay
  mobileOverlay: {
    light: { bg: 'rgba(11,31,24,0.50)', blur: 'blur(8px)' },
    dark: { bg: 'rgba(0,0,0,0.60)', blur: 'blur(12px)' },
  }[resolvedTheme],
  
  // Atmospheric overlays (REDUCED from reference - softer, more restrained)
  atmosphere: {
    light: {
      top: 'radial-gradient(ellipse at center, rgba(15,172,108,0.12) 0%, rgba(142,218,182,0.10) 34%, rgba(11,140,88,0.05) 56%, rgba(11,140,88,0) 80%)',
      topOpacity: 0.74,
      right: 'radial-gradient(ellipse at center, rgba(15,172,108,0.08) 0%, rgba(142,218,182,0.08) 28%, rgba(255,255,255,0.04) 48%, rgba(255,255,255,0) 75%)',
      rightOpacity: 0.48,
      bottom: 'radial-gradient(ellipse at center, rgba(15,172,108,0.06) 0%, rgba(142,218,182,0.06) 34%, rgba(11,140,88,0.03) 52%, rgba(11,140,88,0) 78%)',
      bottomOpacity: 0.40,
      vignetteOpacity: 0.34,
    },
    dark: {
      top: 'radial-gradient(ellipse at center, rgba(15,172,108,0.15) 0%, rgba(142,218,182,0.08) 34%, rgba(11,140,88,0.06) 54%, rgba(11,140,88,0) 80%)',
      topOpacity: 0.68,
      right: 'radial-gradient(ellipse at center, rgba(15,172,108,0.10) 0%, rgba(142,218,182,0.06) 32%, rgba(11,140,88,0.05) 52%, rgba(11,140,88,0) 78%)',
      rightOpacity: 0.42,
      bottom: 'radial-gradient(ellipse at center, rgba(15,172,108,0.09) 0%, rgba(142,218,182,0.05) 32%, rgba(11,140,88,0.04) 50%, rgba(11,140,88,0) 76%)',
      bottomOpacity: 0.38,
      vignetteOpacity: 0.68,
    },
  }[resolvedTheme],
});

/**
 * Premium Nav Item with refined styling
 */
const NavItem = ({ 
  icon: Icon, 
  label, 
  to, 
  children, 
  badge, 
  isNew,
  isPrimary,
  comingSoon,
  openSection, 
  onSectionToggle,
  tokens,
}) => {
  const location = useLocation();
  const hasChildren = children && children.length > 0;
  const isOpen = openSection === label;
  const isActive = to 
    ? location.pathname === to || location.pathname.startsWith(to + '/') 
    : children?.some(c => location.pathname.startsWith(c.to));

  if (comingSoon && !hasChildren) {
    return (
      <div
        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium mb-1 cursor-not-allowed opacity-70"
        style={{ color: tokens.textMuted }}
        data-testid={`selfserve-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon size={20} strokeWidth={1.5} />
        <span>{label}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-white/15 text-white/90">
          Coming soon
        </span>
      </div>
    );
  }

  if (hasChildren) {
    return (
      <div className="mb-1">
        <button
          onClick={() => onSectionToggle(isOpen ? null : label)}
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
          style={{
            background: isActive ? tokens.activeBg : 'transparent',
            color: tokens.text,
          }}
          data-testid={`selfserve-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="flex items-center gap-3">
            <Icon size={20} strokeWidth={1.5} style={{ opacity: isActive ? 1 : 0.8 }} />
            <span>{label}</span>
            {isNew && (
              <span 
                className="px-1.5 py-0.5 text-[10px] rounded uppercase font-bold"
                style={{ background: 'rgba(255,255,255,0.20)', color: '#FFF' }}
              >
                New
              </span>
            )}
          </div>
          <ChevronDown 
            size={16} 
            className={`transition-transform duration-200 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
            style={{ opacity: isActive ? 1 : 0.6 }}
          />
        </button>
        <div 
          className={`overflow-hidden transition-all duration-200 ease-out ${
            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="mt-1 ml-5 pl-3 border-l" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
            {children.map((child, index) => (
              <Link
                key={index}
                to={child.to}
                className="flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-150"
                style={{
                  background: location.pathname === child.to || location.pathname.startsWith(child.to + '/') 
                    ? 'rgba(255,255,255,0.12)' 
                    : 'transparent',
                  color: location.pathname === child.to || location.pathname.startsWith(child.to + '/') 
                    ? tokens.text 
                    : tokens.textMuted,
                }}
              >
                <div className="flex items-center gap-2">
                  {child.icon && <child.icon size={16} />}
                  <span>{child.label}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Primary nav item styling (e.g., Dispute Wizard)
  const primaryStyle = isPrimary && !isActive ? {
    background: 'rgba(15,172,108,0.12)',
    border: '1px solid rgba(15,172,108,0.25)',
  } : {};

  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium mb-1 transition-all duration-200"
      style={{
        background: isActive ? tokens.activeBg : (isPrimary ? 'rgba(15,172,108,0.12)' : 'transparent'),
        color: tokens.text,
        boxShadow: isActive ? '0 2px 8px rgba(15,172,108,0.18)' : 'none',
        border: isPrimary && !isActive ? '1px solid rgba(15,172,108,0.25)' : 'none',
        ...primaryStyle,
      }}
      data-testid={`selfserve-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon size={20} strokeWidth={1.5} style={{ opacity: isActive ? 1 : 0.8 }} />
      <span>{label}</span>
      {badge && (
        <span 
          className="ml-auto px-2 py-0.5 text-xs rounded-full font-medium"
          style={{ background: 'rgba(255,255,255,0.20)', color: '#FFF' }}
        >
          {badge}
        </span>
      )}
      {isNew && (
        <span 
          className="ml-auto px-1.5 py-0.5 text-[10px] rounded uppercase font-bold"
          style={{ background: 'rgba(255,255,255,0.20)', color: '#FFF' }}
        >
          New
        </span>
      )}
      {isPrimary && !isActive && (
        <span 
          className="ml-auto px-1.5 py-0.5 text-[10px] rounded uppercase font-bold"
          style={{ background: 'rgba(15,172,108,0.35)', color: '#FFF' }}
        >
          Start
        </span>
      )}
    </Link>
  );
};

/**
 * Page title mapping
 */
// Helper to get page title key for translation
const getPageTitleKey = (pathname) => {
  const titleKeys = {
    '/dashboard': 'nav.dashboard',
    '/credit-report': 'nav.creditReport',
    '/credit-report/import': 'importFlow.pageTitle',
    '/disputes/rounds': 'nav.roundManager',
    '/disputes/bureau-letters': 'nav.bureauLetters',
    '/disputes/furnisher-letters': 'nav.furnisherLetters',
    '/disputes/ai-letters': 'nav.aiMetro2Letters',
    '/disputes/playbook': 'nav.disputePlaybook',
    '/disputes/tracking': 'nav.disputeTracking',
    '/disputes/history': 'nav.draftHistory',
    '/disputes': 'disputes.centerTitle',
    '/my-disputes': 'disputes.centerTitle',
    '/documents': 'nav.myDocuments',
    '/dispute-wizard': 'nav.disputeWizard',
    '/dispute-wizard/preview': 'disputes.letterPreview',
    '/dispute-wizard/final-package': 'disputes.finalPackagePreview',
    '/credit-builder': 'nav.creditBuilder',
    '/credit-builder/utilization': 'nav.utilizationManager',
    '/credit-builder/tradelines': 'creditBuilder.utilizationManager',
    '/credit-builder/payment-planner': 'nav.paymentPlanner',
    '/credit-builder/payments': 'nav.paymentPlanner',
    '/credit-builder/cli-planner': 'nav.cliPlanner',
    '/credit-builder/simulator': 'creditBuilder.scoreSimulatorNav',
    '/credit-builder/tradeline-strategy': 'creditBuilder.tradelineStrategyNav',
    '/blueprints': 'nav.blueprints',
    '/blueprints/authorized-user': 'blueprints.authorizedUser',
    '/blueprints/credit-union': 'blueprints.creditUnion',
    '/blueprints/secured-loan': 'blueprints.securedLoan',
    '/academy': 'nav.academy',
    '/settings': 'nav.settings',
    '/settings/connections': 'nav.connections',
  };
  
  const exactMatch = titleKeys[pathname];
  if (exactMatch) return exactMatch;
  
  const sortedRoutes = Object.entries(titleKeys).sort((a, b) => b[0].length - a[0].length);
  for (const [path, key] of sortedRoutes) {
    if (pathname.startsWith(path)) return key;
  }
  if (pathname.startsWith('/disputes') || pathname.startsWith('/my-disputes')) {
    return 'disputes.centerTitle';
  }
  
  return 'nav.dashboard';
};

/**
 * Self-Serve Portal Layout - Premium Refined with Theme Support
 */
const SelfServePortalLayoutInner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { supportContext, isInSupportMode } = useSupportContext();
  const { t, theme: i18nTheme, setTheme: setI18nTheme } = useI18n();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const { theme, resolvedTheme, setTheme } = useTheme();
  const tokens = getThemeTokens(resolvedTheme);

  const handleThemeChange = useCallback(
    (value) => {
      setTheme(value);
      setI18nTheme(value);
    },
    [setTheme, setI18nTheme]
  );
  
  // Sync I18n theme with local theme
  useEffect(() => {
    if (i18nTheme && i18nTheme !== 'system') {
      setTheme(i18nTheme);
    }
  }, [i18nTheme, setTheme]);
  
  const effectiveClientName = isInSupportMode 
    ? (supportContext?.effective_user_name || 'Customer')
    : null;
  const isViewingAsSupport = isInSupportMode && supportContext?.effective_user_id;

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Auto-expand section based on current path
  useEffect(() => {
    if (location.pathname.includes('/credit-report')) setOpenSection(t('nav.creditReport'));
    else if (location.pathname.includes('/credit-builder')) setOpenSection(t('nav.creditBuilder'));
    else if (location.pathname.includes('/dispute-wizard')) setOpenSection(t('nav.disputeWizard'));
  }, [location.pathname, t]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const portalHost = getPortalFromHostname();
  const disputesPath =
    portalHost === PORTAL_SURFACES.SELF_REPAIR ? '/my-disputes' : '/disputes';

  // Navigation structure for Self-Serve / Auto Starter
  // Order: Dashboard > Credit Report > Dispute Wizard > My Disputes > My Documents > Credit Builder > Blueprints > Academy > Settings
  // NOTE: Send Letter removed - all send/mail/export now happens through Dispute Wizard (Steps 5–7)
  const navItems = [
    { icon: LayoutDashboard, label: t('nav.dashboard'), to: '/dashboard' },
    {
      icon: FileText,
      label: t('nav.creditReport'),
      children: [
        { label: t('creditReport.importReport'), to: '/credit-report/import', icon: Import },
        { label: t('creditReport.title'), to: '/credit-report', icon: BarChart3 },
      ],
    },
    { icon: Wand2, label: t('nav.disputeWizard'), to: '/dispute-wizard', isPrimary: true },
    { icon: Gavel, label: t('nav.disputeCenter'), to: disputesPath },
    { icon: FolderOpen, label: t('nav.myDocuments'), to: '/documents' },
    {
      icon: TrendingUp,
      label: t('nav.creditBuilder'),
      children: [
        { label: t('creditBuilder.utilizationManager'), to: '/credit-builder/tradelines', icon: Calculator },
        { label: t('nav.cliPlanner'), to: '/credit-builder/cli-planner', icon: CreditCard },
        { label: t('nav.paymentPlanner'), to: '/credit-builder/payment-planner', icon: CalendarCheck },
        { label: t('creditBuilder.scoreSimulatorNav'), to: '/credit-builder/simulator', icon: LineChart },
        { label: t('creditBuilder.tradelineStrategyNav'), to: '/credit-builder/tradeline-strategy', icon: Zap },
      ],
    },
    {
      icon: Map,
      label: t('nav.blueprints'),
      children: [
        { label: 'AU Blueprint', to: '/blueprints/authorized-user', icon: Users },
        { label: 'Credit Union Blueprint', to: '/blueprints/credit-union', icon: Building2 },
        { label: 'Secured Loan Blueprint', to: '/blueprints/secured-loan', icon: Shield },
        { label: 'Rent Reporting Blueprint', to: '/blueprints/rent-reporting', icon: Home },
      ],
    },
    { icon: GraduationCap, label: t('nav.academy'), to: '/academy' },
    { icon: Settings, label: t('nav.settings'), to: '/settings' },
  ];

  const pageTitle = t(getPageTitleKey(location.pathname));

  return (
    <div 
      className="min-h-screen relative" 
      style={{ 
        background: `linear-gradient(180deg, ${tokens.bg.bg1} 0%, ${tokens.bg.bg0} 100%)`,
      }}
    >
      {/* Atmospheric Overlays - REDUCED from reference for softer effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        {/* Top overlay - centered ellipse (reduced from 1120×230 to 1080×220) */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '-52px',
            width: '1080px',
            height: '220px',
            transform: 'translateX(-50%)',
            borderRadius: '999px',
            background: tokens.atmosphere.top,
            filter: 'blur(56px)',
            opacity: tokens.atmosphere.topOpacity,
            transition: 'all 0.22s ease',
          }}
        />
        {/* Right support glow (reduced from 420×72% to 390×66%) */}
        <div
          style={{
            position: 'absolute',
            right: '-110px',
            top: '130px',
            width: '390px',
            height: '66%',
            borderRadius: '999px',
            background: tokens.atmosphere.right,
            filter: 'blur(66px)',
            opacity: tokens.atmosphere.rightOpacity,
            transition: 'all 0.22s ease',
          }}
        />
        {/* Bottom support glow */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: '-120px',
            width: '980px',
            height: '220px',
            transform: 'translateX(-50%)',
            borderRadius: '999px',
            background: tokens.atmosphere.bottom,
            filter: 'blur(76px)',
            opacity: tokens.atmosphere.bottomOpacity,
            transition: 'all 0.22s ease',
          }}
        />
        {/* Edge vignette for depth */}
        <div 
          style={{ 
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at center, rgba(0,0,0,0) 44%, rgba(0,0,0,0.08) 100%)',
            opacity: tokens.atmosphere.vignetteOpacity,
          }}
        />
      </div>
      
      {/* Support Mode Banner */}
      <SupportModeBanner />
      
      {/* Premium Sidebar - Desktop always visible, mobile slide-in drawer */}
      <aside 
        className={`fixed inset-y-0 left-0 z-40 w-64 transform transition-all duration-300 ease-out flex flex-col
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{
          background: tokens.sidebar.gradient,
          boxShadow: '4px 0 24px rgba(16,24,40,0.08)',
        }}
        data-testid="selfserve-sidebar"
      >
        {/* Right edge separator */}
        <div 
          className="absolute top-0 right-0 bottom-0 w-px"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%)' }}
        />

        {/* Logo Area */}
        <div className="p-5 mb-2 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center" data-testid="selfserve-sidebar-logo">
            <FundEliteBrandMark
              variant="self-repair"
              mode="logo"
              theme={resolvedTheme}
              logoClassName="h-9 w-auto max-w-[168px] object-contain object-left"
            />
          </Link>
        </div>

        {/* Subtle divider */}
        <div className="mx-4 h-px mb-3" style={{ background: 'rgba(255,255,255,0.10)' }} />

        {/* Navigation */}
        <nav 
          className="flex-1 px-3 pb-4 overflow-y-auto"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.3) transparent' }}
        >
          {navItems.map((item, index) => (
            <NavItem
              key={index}
              {...item}
              openSection={openSection}
              onSectionToggle={setOpenSection}
              tokens={tokens.sidebar}
            />
          ))}
        </nav>

        {/* User Profile Area */}
        <div 
          className="m-3 p-3 rounded-xl flex-shrink-0"
          style={{ 
            background: 'rgba(255,255,255,0.10)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2" style={{ borderColor: 'rgba(255,255,255,0.25)' }}>
              <AvatarImage src={user?.picture} />
              <AvatarFallback 
                className="text-white font-semibold text-sm"
                style={{ background: 'rgba(255,255,255,0.15)' }}
              >
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name || 'User'}</p>
              <p className="text-xs truncate" style={{ color: tokens.sidebar.textMuted }}>{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg transition-colors hover:bg-white/10"
              style={{ color: tokens.sidebar.textMuted }}
              data-testid="selfserve-logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Menu Overlay - Premium blur treatment */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-30 lg:hidden transition-all duration-300"
          style={{ 
            background: tokens.mobileOverlay.bg,
            backdropFilter: tokens.mobileOverlay.blur,
            WebkitBackdropFilter: tokens.mobileOverlay.blur,
          }}
          onClick={() => setMobileMenuOpen(false)}
        >
          {/* Subtle mint atmosphere */}
          <div 
            className="absolute top-1/4 left-1/2 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{ background: '#8EDAB6', transform: 'translate(-50%, -50%)' }}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="lg:ml-64 min-w-0 overflow-x-hidden">
        {/* Glass Top Bar - Premium transparent treatment */}
        <header 
          className="sticky top-0 z-20 px-4 lg:px-6 py-3"
          style={{ 
            background: tokens.topBar.bg,
            backdropFilter: tokens.topBar.blur,
            WebkitBackdropFilter: tokens.topBar.blur,
            borderBottom: `1px solid ${tokens.topBar.border}`,
            boxShadow: tokens.topBar.shadow,
          }}
        >
          {/* Top edge highlight for glass effect */}
          <div 
            className="absolute top-0 left-0 right-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)' }}
          />
          
          <div className="flex items-center justify-between">
            {/* Left: Mobile menu + Page title only (no welcome redundancy) */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg transition-colors"
                style={{ color: tokens.topBar.text }}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <div>
                {isViewingAsSupport ? (
                  <>
                    <h2 className="text-lg font-semibold" style={{ color: tokens.topBar.text }}>
                      Viewing: {effectiveClientName}
                    </h2>
                    <p className="text-sm" style={{ color: tokens.topBar.textMuted }}>{t('topBar.supportContextActive')}</p>
                  </>
                ) : (
                  <>
                    <h2 className="text-lg font-semibold" style={{ color: tokens.topBar.text }}>
                      {pageTitle}
                    </h2>
                    <p className="text-xs" style={{ color: tokens.topBar.textMuted }}>
                      {t('topBar.yourCreditRepairJourney')}
                    </p>
                  </>
                )}
              </div>
            </div>
            
            {/* Right: notifications + profile menu */}
            <div className="flex items-center gap-2">
              <NotificationDropdown tokens={tokens} />
              <ProfileMenuDropdown
                user={user}
                tokens={tokens}
                onLogout={handleLogout}
                resolvedTheme={resolvedTheme}
                theme={theme}
                onThemeChange={handleThemeChange}
              />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6 min-w-0 max-w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>

    </div>
  );
};

/**
 * Wrapped with Theme Provider
 */
const SelfServePortalLayout = () => (
  <ThemeProvider>
    <SelfServePortalLayoutInner />
  </ThemeProvider>
);

export default SelfServePortalLayout;
